import {
    METRICS_TO_RECORD,
    METRIC_NAME_MAPPING,
    METRIC_TYPE_HISTOGRAM,
    type MetricDefinition,
} from './metrics';
import { validateLabelsAndName } from './label-util';

/**
 * Interface for the PrometheusBatcher from the web repo.
 * Only includes methods used by LuminaryActivityProfiler.
 */
interface PrometheusBatcher {
    register(metricDefn: MetricDefinition): void;
    overrideContext(context: string): void;
    observe(metricDefn: MetricDefinition, value: number, labels: Record<string, string>): void;
}

interface GlobalWithProfiler {
    LuminaryActivityProfiler?: LuminaryActivityProfiler;
}

declare global {
    interface Window {
        ActivityProfiler?: { prometheus?: PrometheusBatcher };
    }
}

let NODE = false;
            
            
          

/**
 *
 * This singleton class handles registering metrics and timing interactions.
 * See ./metrics.js for examples on how to defined your own metrics.
 *
 * @example
 * import LuminaryActivityProfiler from './LuminaryActivityProfiler'
 * // A single instance of this class is created and attached to the global context. The default
 * // export of this file is that instance.
 *
 * @example
 * const uuid = generateUuid();
 * LuminaryActivityProfiler.start(uuid);
 * // any code you want to time. it does not have to be synchronous - as long as the caller can supply the
 * // same uuid to the end call, the same event will be timed
 * LuminaryActivityProfiler.end(uuid, metricName, eventLabels)
 *
 */
class LuminaryActivityProfiler {
    static #instance: LuminaryActivityProfiler;

    /**
     * maps event IDs to a timestamp that represents when the event started
     */
    #startTimeMap = new Map();

    prometheus: PrometheusBatcher | undefined = undefined;

    #isContextBrowser = false;

    static getInstance() {
        if (LuminaryActivityProfiler.#instance) {
            return LuminaryActivityProfiler.#instance;
        }
        LuminaryActivityProfiler.#instance = new LuminaryActivityProfiler();
        return LuminaryActivityProfiler.#instance;
    }

    constructor() {
        this.#connectPrometheusIfAvailable();
    }

    /**
     * We should use the global ActivityProfiler instance of PrometheusBatcher instance (from the `web` repo).
     * It's only available in the browser.
     */
    #connectPrometheusIfAvailable() {
        if (this.prometheus !== undefined) {
            return;
        }

        if (window.ActivityProfiler && window.ActivityProfiler.prometheus) {
            const prometheus = window.ActivityProfiler.prometheus;
            this.prometheus = prometheus;
            this.#isContextBrowser = true;

            // creates a metric class instance for each prometheus metric to record
            METRICS_TO_RECORD.forEach((metricDefn) => prometheus.register(metricDefn));
        }
    }

    overrideContext(newContext: string) {
        if (!this.#isContextBrowser && !NODE) {
            // this is node express app
            return;
        }
        if (!this.prometheus) {
            // this is node
            return;
        }
        this.prometheus.overrideContext(newContext);
    }

    /**
     * Starts timing the execution of an event, identified by a unique id
     *
     * @param id : a unique identifier for the event which should be used to end the timing
     */
    start(id: string) {
        this.#connectPrometheusIfAvailable();
        if (!this.#isContextBrowser && !NODE) {
            return;
        }
        if (!id) {
            return;
        }
        this.#startTimeMap.set(id, performance.now());
    }

    /**
     * Ends timing for event previously identified by an id. Stores the observed value internally
     * to be sent to prometheus later
     *
     * @param id an existing uuid used in a past call to start()
     * @param metricName : a const metric name defined in ./metrics.js
     * @param eventLabels : key value pairs of labels for the specific metric name. See .metrics.js for
     *  each event's expected labels and label values.
     */
    end(id: string, metricName: string, eventLabels: Record<string, string>) {
        if (!this.#isContextBrowser && !NODE) {
            return;
        }
        const end = performance.now();
        // async execute all non-timing logic for performance
        if (!this.#startTimeMap.has(id)) {
            throw new Error(
                "LuminaryActivityProfiler received an end timing call for an event that doesn't exist",
            );
        }
        if (!validateLabelsAndName(metricName, eventLabels)) {
            throw new Error(`
                LuminaryActivityProfiler is not recording performance of ${metricName} because of an unexpected metric name or label set.
            `);
        }
        const metricDefn = METRIC_NAME_MAPPING[metricName];
        const measuredTime = end - this.#startTimeMap.get(id);
        if (this.#isContextBrowser) {
            this.prometheus!.observe(metricDefn, measuredTime, eventLabels);
        } else if (metricDefn.type === METRIC_TYPE_HISTOGRAM) {
            metricDefn.node!.record(measuredTime, eventLabels);
        }
        this.#startTimeMap.delete(id);
    }
}

/**
 * Singleton implementation to export and create only one instance that
 * is attached to the global context
 */
function getGlobal(): GlobalWithProfiler {
    if (typeof window !== 'undefined') {
        return window as GlobalWithProfiler;
    } else if (typeof global !== 'undefined') {
        return global as GlobalWithProfiler;
    } else if (typeof self !== 'undefined') {
        return self as GlobalWithProfiler;
    } else {
        throw new Error('unable to find global context');
    }
}

let profilerInstance: LuminaryActivityProfiler;

const globalContext = getGlobal();
if (globalContext.LuminaryActivityProfiler) {
    profilerInstance = globalContext.LuminaryActivityProfiler;
} else {
    profilerInstance = LuminaryActivityProfiler.getInstance();
    globalContext.LuminaryActivityProfiler = profilerInstance;
}

export default profilerInstance;
